<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { Button, useBreakpoints } from '@collector/shared-ui'
import { Incident } from '@collector/sportsapi-client'
import {
  useEventSettings,
  useTeamSides,
} from '@desktop/views/Relation/composables'
import { useEventIncidentsQueue } from '@desktop/views/Relation/Sports/composables'
import {
  injectEventStatusIncidentsOnFieldConfig,
  injectHasChangeSides,
} from '@desktop/views/Relation/Sports/provide-inject'

import ChangeSidesButton from './ChangeSidesButton.vue'
import ParticipantButton from './ParticipantButton.vue'
import { useIncidentToSend } from './useIncidentToSend'

interface Emits {
  visible: [visible: boolean]
}

const emit = defineEmits<Emits>()

// inject
const { eventStatusToIncidentIdMap, startingSide, secondHalfIncidentId } =
  injectEventStatusIncidentsOnFieldConfig()

const hasChangeSides = injectHasChangeSides()

// composables
const { updateEventSettings } = useEventSettings()
const eventIncidentsQueue = useEventIncidentsQueue()
const { getParticipantsWithCurrentTeamSides } = useTeamSides()
const { currentFirstParticipant, currentSecondParticipant } =
  getParticipantsWithCurrentTeamSides()

const { incidentToSend, getTeamSidePossession } = useIncidentToSend(
  eventStatusToIncidentIdMap,
)
const { breakpoints } = useBreakpoints()

// state
const shouldShowIncidentToSend = ref(false)

// computed
const shouldShowChooseSide = computed(
  () => incidentToSend.value?.id === startingSide?.incidentId,
)

// methods
async function sendEventStatusIncident(
  participantTeamId?: Incident['participant_team_id'],
): Promise<void> {
  const incidentId = incidentToSend.value!.id

  try {
    // immediately set to null to hide buttons and prevent multiple clicks which send duplicated incidents
    shouldShowIncidentToSend.value = false

    await eventIncidentsQueue.addIncident(
      {
        incident_id: incidentId,
        ...(participantTeamId && { participant_team_id: participantTeamId }),
      },
      {
        shouldResetPossession: true,
      },
    )
  } catch (error) {
    // if request failed, show again buttons with incident to send
    shouldShowIncidentToSend.value = true

    throw error
  }

  if (startingSide && secondHalfIncidentId) {
    const teamSidePossession = getTeamSidePossession(
      incidentId,
      participantTeamId,
      startingSide?.incidentId,
      secondHalfIncidentId,
    )

    if (teamSidePossession) {
      await updateEventSettings({ teamSidePossession })
    }
  }
}

// init
watch(
  incidentToSend,
  () => {
    if (incidentToSend.value) {
      shouldShowIncidentToSend.value = true
    }

    emit('visible', !!incidentToSend.value)
  },
  {
    immediate: true,
  },
)
</script>

<template>
  <div
    v-if="incidentToSend"
    class="bg-neutral-dark-0/60 absolute top-0 h-full w-full select-none"
  >
    <Transition>
      <div v-if="shouldShowIncidentToSend">
        <ChangeSidesButton
          v-if="hasChangeSides"
          class="z-20"
        />
        <div
          class="absolute top-1/2 mt-4 flex w-full -translate-y-1/2 flex-col items-center gap-y-2 p-1"
        >
          <template v-if="startingSide && shouldShowChooseSide">
            <div
              class="text-neutral-light-10 text-2xs text-center font-bold text-nowrap @lg:text-base"
            >
              {{ startingSide.text }}
            </div>
            <div class="flex w-full gap-x-6 px-2">
              <ParticipantButton
                v-if="currentFirstParticipant"
                class="w-1/2 px-3"
                :participant="currentFirstParticipant"
                :size="breakpoints['2md'].value ? 'lg' : 'sm'"
                data-testid="event-status-incident-button-home"
                @click="sendEventStatusIncident(currentFirstParticipant.id)"
              />
              <ParticipantButton
                v-if="currentSecondParticipant"
                class="w-1/2 px-3"
                :participant="currentSecondParticipant"
                :size="breakpoints['2md'].value ? 'lg' : 'sm'"
                data-testid="event-status-incident-button-away"
                @click="sendEventStatusIncident(currentSecondParticipant.id)"
              />
            </div>
          </template>
          <Button
            v-else
            class="bg-neutral-light-9 border-neutral-light-0 hover:bg-neutral-light-10 mt-5 w-9/10 font-bold text-wrap! hover:border-transparent"
            :size="breakpoints['2md'].value ? 'md' : 'sm'"
            :fontSize="breakpoints['2md'].value ? 'text-sm' : 'text-2xs'"
            width="full"
            @click="sendEventStatusIncident()"
          >
            Send "{{ incidentToSend.name }}"
          </Button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
